/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import {Platform} from '@angular/cdk/platform';
import {
  CanColor,
  CanDisable,
  CanDisableRipple,
  MATERIAL_COMPATIBILITY_MODE,
  mixinColor,
  mixinDisabled,
  mixinDisableRipple
} from '@angular/material/core';
import {FocusMonitor} from '@angular/cdk/a11y';
/**
 * Default color palette for round buttons (md-fab and md-mini-fab)
 */
const DEFAULT_ROUND_BUTTON_COLOR = 'accent';
/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-button], button[mat-button], a[md-button], a[mat-button]',
  host: {'class': 'mat-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdRaisedButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'button[md-raised-button], button[mat-raised-button], ' +
      'a[md-raised-button], a[mat-raised-button]',
  host: {'class': 'mat-raised-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdRaisedButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdRaisedButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdRaisedButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdIconButtonCssMatStyler {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'button[md-icon-button], button[mat-icon-button], a[md-icon-button], a[mat-icon-button]',
  host: {'class': 'mat-icon-button'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdIconButtonCssMatStyler_tsickle_Closure_declarations() {
/** @type {?} */
MdIconButtonCssMatStyler.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdIconButtonCssMatStyler.ctorParameters;
}

/**
 * Directive whose purpose is to add the mat- CSS styling to this selector.
 * \@docs-private
 */
export class MdFab {
/**
 * @param {?} button
 * @param {?} anchor
 */
constructor(   button: MdButton,
                 anchor: MdAnchor) {
    // Set the default color palette for the md-fab components.
    (button || anchor).color = DEFAULT_ROUND_BUTTON_COLOR;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-fab], button[mat-fab], a[md-fab], a[mat-fab]',
  host: {'class': 'mat-fab'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdButton, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdButton), ] }, ]},
{type: MdAnchor, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdAnchor), ] }, ]},
];
}

function MdFab_tsickle_Closure_declarations() {
/** @type {?} */
MdFab.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdFab.ctorParameters;
}

/**
 * Directive that targets mini-fab buttons and anchors. It's used to apply the `mat-` class
 * to all mini-fab buttons and also is responsible for setting the default color palette.
 * \@docs-private
 */
export class MdMiniFab {
/**
 * @param {?} button
 * @param {?} anchor
 */
constructor(   button: MdButton,
                 anchor: MdAnchor) {
    // Set the default color palette for the md-mini-fab components.
    (button || anchor).color = DEFAULT_ROUND_BUTTON_COLOR;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'button[md-mini-fab], button[mat-mini-fab], a[md-mini-fab], a[mat-mini-fab]',
  host: {'class': 'mat-mini-fab'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdButton, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdButton), ] }, ]},
{type: MdAnchor, decorators: [{ type: Self }, { type: Optional }, { type: Inject, args: [forwardRef(() => MdAnchor), ] }, ]},
];
}

function MdMiniFab_tsickle_Closure_declarations() {
/** @type {?} */
MdMiniFab.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMiniFab.ctorParameters;
}

/**
 * \@docs-private
 */
export class MdButtonBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdButtonBase_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonBase.prototype._renderer;
/** @type {?} */
MdButtonBase.prototype._elementRef;
}

export const /** @type {?} */ _MdButtonMixinBase = mixinColor(mixinDisabled(mixinDisableRipple(MdButtonBase)));
/**
 * Material design button.
 */
export class MdButton extends _MdButtonMixinBase
    implements OnDestroy, CanDisable, CanColor, CanDisableRipple {
/**
 * Whether the button is round.
 */
_isRoundButton: boolean = this._hasAttributeWithPrefix('fab', 'mini-fab');
/**
 * Whether the button is icon button.
 */
_isIconButton: boolean = this._hasAttributeWithPrefix('icon-button');
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} _platform
 * @param {?} _focusMonitor
 */
constructor(renderer: Renderer2,
              elementRef: ElementRef,
private _platform: Platform,
private _focusMonitor: FocusMonitor) {
    super(renderer, elementRef);
    this._focusMonitor.monitor(this._elementRef.nativeElement, this._renderer, true);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef.nativeElement);
  }
/**
 * Focuses the button.
 * @return {?}
 */
focus(): void {
    this._getHostElement().focus();
  }
/**
 * @return {?}
 */
_getHostElement() {
    return this._elementRef.nativeElement;
  }
/**
 * @return {?}
 */
_isRippleDisabled() {
    return this.disableRipple || this.disabled;
  }
/**
 * Gets whether the button has one of the given attributes
 * with either an 'md-' or 'mat-' prefix.
 * @param {...?} unprefixedAttributeNames
 * @return {?}
 */
_hasAttributeWithPrefix(...unprefixedAttributeNames: string[]) {
    // If not on the browser, say that there are none of the attributes present.
    // Since these only affect how the ripple displays (and ripples only happen on the client),
    // detecting these attributes isn't necessary when not on the browser.
    if (!this._platform.isBrowser) {
      return false;
    }

    return unprefixedAttributeNames.some(suffix => {
      const /** @type {?} */ el = this._getHostElement();

      return el.hasAttribute('md-' + suffix) || el.hasAttribute('mat-' + suffix);
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: `button[md-button], button[md-raised-button], button[md-icon-button],
             button[md-fab], button[md-mini-fab],
             button[mat-button], button[mat-raised-button], button[mat-icon-button],
             button[mat-fab], button[mat-mini-fab]`,
  host: {
    '[disabled]': 'disabled || null',
  },
  templateUrl: 'button.html',
  styleUrls: ['button.css'],
  inputs: ['disabled', 'disableRipple', 'color'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: Platform, },
{type: FocusMonitor, },
];
}

function MdButton_tsickle_Closure_declarations() {
/** @type {?} */
MdButton.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButton.ctorParameters;
/**
 * Whether the button is round.
 * @type {?}
 */
MdButton.prototype._isRoundButton;
/**
 * Whether the button is icon button.
 * @type {?}
 */
MdButton.prototype._isIconButton;
/** @type {?} */
MdButton.prototype._platform;
/** @type {?} */
MdButton.prototype._focusMonitor;
}

/**
 * Raised Material design button.
 */
export class MdAnchor extends MdButton {
/**
 * @param {?} platform
 * @param {?} focusMonitor
 * @param {?} elementRef
 * @param {?} renderer
 */
constructor(
      platform: Platform,
      focusMonitor: FocusMonitor,
      elementRef: ElementRef,
      renderer: Renderer2) {
    super(renderer, elementRef, platform, focusMonitor);
  }
/**
 * @param {?} event
 * @return {?}
 */
_haltDisabledEvents(event: Event) {
    // A disabled button shouldn't apply any actions
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: `a[md-button], a[md-raised-button], a[md-icon-button], a[md-fab], a[md-mini-fab],
             a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab], a[mat-mini-fab]`,
  host: {
    '[attr.tabindex]': 'disabled ? -1 : 0',
    '[attr.disabled]': 'disabled || null',
    '[attr.aria-disabled]': 'disabled.toString()',
    '(click)': '_haltDisabledEvents($event)',
  },
  inputs: ['disabled', 'disableRipple', 'color'],
  templateUrl: 'button.html',
  styleUrls: ['button.css'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Platform, },
{type: FocusMonitor, },
{type: ElementRef, },
{type: Renderer2, },
];
}

function MdAnchor_tsickle_Closure_declarations() {
/** @type {?} */
MdAnchor.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdAnchor.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
